import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Drawer from '@mui/material/Drawer';

import api from "../../components/api";
import DraggableTableIcon, {
  TableType,
} from "../../components/DroppableTableIcon/DroppableTableIcon";
import "./WeddingTableLayoutPage.css";
import roundTable from "../../icons/round_table.svg";
import longTable from "../../icons/long_table.svg";
import stage from "../../icons/stage.svg";
import circle from "../../icons/circle.svg";
import curvedTable from "../../icons/curved_table.svg";
import danceFloor from "../../icons/dance_floor.svg";
import sShapedTable from "../../icons/S_shaped_table.svg";
import { FontSelector } from "../../components/SeatingChart/StyleSidebar";


const GRID_SIZE = 20; // 网格在屏幕上的像素大小
const REAL_GRID_SIZE = 50; // 每个网格代表的实际厘米数

const calculatePixelsPerCm = () => {
  return GRID_SIZE / REAL_GRID_SIZE; // 每厘米对应的像素数
};

interface ImageType {
  id: string;
  url: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface DraggableImageProps {
  image: ImageType;
  onDragEnd: (id: string, x: number, y: number) => void;
  onResize: (id: string, width: number, height: number) => void;
  onDelete: (id: string) => void;
}

const DraggableImage: React.FC<DraggableImageProps> = ({ image, onDragEnd, onResize, onDelete }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dimensions, setDimensions] = useState({ width: image.width, height: image.height });
  const imageRef = React.useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const rect = imageRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
    }
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && imageRef.current) {
        const parent = imageRef.current.offsetParent as HTMLElement;
        if (parent) {
          const parentRect = parent.getBoundingClientRect();
          const x = e.clientX - parentRect.left - dragOffset.x;
          const y = e.clientY - parentRect.top - dragOffset.y;
          imageRef.current.style.left = `${x}px`;
          imageRef.current.style.top = `${y}px`;
        }
      } else if (isResizing && imageRef.current) {
        const rect = imageRef.current.getBoundingClientRect();
        const newWidth = e.clientX - rect.left;
        const newHeight = e.clientY - rect.top;
        setDimensions({
          width: Math.max(50, newWidth),
          height: Math.max(50, newHeight)
        });
      }
    };

    const handleMouseUp = () => {
      if (isDragging && imageRef.current) {
        const left = parseInt(imageRef.current.style.left || '0');
        const top = parseInt(imageRef.current.style.top || '0');
        onDragEnd(image.id, left, top);
      } else if (isResizing) {
        onResize(image.id, dimensions.width, dimensions.height);
      }
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragOffset, dimensions, image.id, onDragEnd, onResize]);

  return (
    <div
      ref={imageRef}
      className="draggable-image"
      style={{
        position: 'absolute',
        left: `${image.x}px`,
        top: `${image.y}px`,
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
        zIndex: 0
      }}
      onMouseDown={handleMouseDown}
    >
      <img
        src={image.url}
        alt="Uploaded content"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain'
        }}
      />
      <div
        className="resize-handle"
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '16px',
          height: '16px',
          cursor: 'nwse-resize',
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }}
        onMouseDown={handleResizeStart}
      />
      <button
        onClick={() => onDelete(image.id)}
        style={{
          position: 'absolute',
          top: '5px',
          right: '5px',
          cursor: 'pointer',
          background: 'red',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        ×
      </button>
    </div>
  );
};

const fontOptions = [
  { value: "Dancing Script", label: "Dancing Script" },
  { value: "Playfair Display", label: "Playfair Display" },
  { value: "Lora", label: "Lora" },
  { value: "Pacifico", label: "Pacifico" },
  { value: "Satisfy", label: "Satisfy" },
  { value: "Mea Culpa", label: "Mea Culpa" },
  { value: "Fleur De Leah", label: "Fleur De Leah" },
];

// 定义桌子样式映射
const tableStyleMap = {
  1: roundTable,
  2: longTable,
  3: stage,
  4: circle,
  5: curvedTable,
  6: danceFloor,
  7: sShapedTable,
} as const;

function WeddingTableLayoutPage() {
  const navigate = useNavigate();
  const { weddingTableId } = useParams<{ weddingTableId: string }>();
  const [tables, setTables] = useState<TableType[]>([]);
  const [images, setImages] = useState<ImageType[]>([]);

  const [layoutWidth, setLayoutWidth] = useState<number>(1550);
  const [layoutHeight, setLayoutHeight] = useState<number>(1550);

  const [scalePosition, setScalePosition] = useState({
    x: window.innerWidth - 100,
    y: window.innerHeight - 100,
  });

  const [drawerOpen, setDrawerOpen] = useState(false);

  const [coupleNames, setCoupleNames] = useState<string>("");
  const [coupleNamesFont, setCoupleNamesFont] = useState<string>("Arial");
  const [coupleNamesFontSize, setCoupleNamesFontSize] = useState<number>(16);
  const [coupleNamesPosition, setCoupleNamesPosition] = useState<{ x: number, y: number }>({ x: 0, y: 20 });

  const [weddingDate, setWeddingDate] = useState<string>("");
  const [weddingDateFont, setWeddingDateFont] = useState<string>("Lora");
  const [weddingDateFontSize, setWeddingDateFontSize] = useState<number>(14);
  const [weddingDatePosition, setWeddingDatePosition] = useState<{ x: number, y: number }>({ x: 0, y: 50 });

  const [selectedTable, setSelectedTable] = useState<TableType | null>(null);

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const applyTableStyles = useCallback(() => {
    setTimeout(() => {
      tables.forEach(table => {
        const tableElement = document.querySelector(`.table-content.draggable-table[data-id="${table.id}"]`);
        if (tableElement) {
          const rotation = table.rotation || 0;
          const flipX = table.flipX ? -1 : 1;
          const flipY = table.flipY ? -1 : 1;
          
          const transform = `rotate(${rotation}deg) scaleX(${flipX}) scaleY(${flipY})`;
          (tableElement as HTMLElement).style.transform = transform;
        }
      });
    }, 100);
  }, [tables]);

  const applyTableNameCorrection = useCallback(() => {
    setTimeout(() => {
      tables.forEach(table => {
        const tableElement = document.querySelector(`.table-content.draggable-table[data-id="${table.id}"]`);
        if (tableElement) {
          const flipX = table.flipX ? -1 : 1;
          const flipY = table.flipY ? -1 : 1;
          
          // 查找桌子名称元素
          const nameElement = tableElement.querySelector('div[style*="transform: translate(-50%, -50%)"]');
          
          if (nameElement) {
            // 直接修复桌子名称的翻转
            (nameElement as HTMLElement).style.position = 'absolute';
            (nameElement as HTMLElement).style.top = '50%';
            (nameElement as HTMLElement).style.left = '50%';
            (nameElement as HTMLElement).style.transform = `translate(-50%, -50%) scaleX(${flipX}) scaleY(${flipY})`;
            (nameElement as HTMLElement).style.textAlign = 'center';
          } else {
            // 如果找不到精确的名称元素，尝试查找所有div并检查transform
            const divElements = tableElement.querySelectorAll('div');
            divElements.forEach(div => {
              const style = window.getComputedStyle(div);
              if (style.transform && style.transform.includes('translate')) {
                div.style.transform = `${style.transform} scaleX(${flipX}) scaleY(${flipY})`;
              }
            });
          }
        }
      });
    }, 100);
  }, [tables]);

  const loadTableStyle = useCallback(() => {
    if (weddingTableId) {
      api.weddings.getWeddingWeddingTableWeddingTableIdGet(weddingTableId).then((data) => {
        if (data.data.wedding_style) {
          try {
            const wedding_style = JSON.parse(data.data.wedding_style);
            setLayoutWidth(wedding_style.width || 1550);
            setLayoutHeight(wedding_style.height || 1550);
            
            const pixelsPerCm = GRID_SIZE / REAL_GRID_SIZE;
            
            // 加载图片信息
            if (wedding_style.images) {
              setImages(wedding_style.images.map((img: any) => ({
                ...img,
                x: img.x * pixelsPerCm,
                y: img.y * pixelsPerCm,
                width: img.width * pixelsPerCm,
                height: img.height * pixelsPerCm
              })));
            }
            
            // 加载新人姓名和字体设置
            if (wedding_style.coupleNames) {
              setCoupleNames(wedding_style.coupleNames);
            }
            if (wedding_style.coupleNamesFont) {
              setCoupleNamesFont(wedding_style.coupleNamesFont);
            }
            if (wedding_style.coupleNamesFontSize) {
              setCoupleNamesFontSize(wedding_style.coupleNamesFontSize);
            }
            // 加载新人姓名位置
            if (wedding_style.coupleNamesPosition) {
              setCoupleNamesPosition({
                x: wedding_style.coupleNamesPosition.x * pixelsPerCm,
                y: wedding_style.coupleNamesPosition.y * pixelsPerCm
              });
            } else {
              // 默认位置设在顶部中间
              setCoupleNamesPosition({ 
                x: (wedding_style.width || 1550) * pixelsPerCm / 2 - 100, 
                y: 20 
              });
            }

            // 加载婚礼日期和字体设置
            if (wedding_style.weddingDate) {
              setWeddingDate(wedding_style.weddingDate);
            }
            if (wedding_style.weddingDateFont) {
              setWeddingDateFont(wedding_style.weddingDateFont);
            }
            if (wedding_style.weddingDateFontSize) {
              setWeddingDateFontSize(wedding_style.weddingDateFontSize);
            }
            // 加载婚礼日期位置
            if (wedding_style.weddingDatePosition) {
              setWeddingDatePosition({
                x: wedding_style.weddingDatePosition.x * pixelsPerCm,
                y: wedding_style.weddingDatePosition.y * pixelsPerCm
              });
            } else {
              // 默认位置设在新人姓名下方
              setWeddingDatePosition({ 
                x: (wedding_style.width || 1550) * pixelsPerCm / 2 - 100, 
                y: 60 
              });
            }
          } catch (e) {
            console.warn("Invalid wedding_style JSON:", e);
          }
        }
      });

      api.tables
        .getWeddingsWeddingTableIdTablesWeddingTableWeddingTableIdTablesGet(weddingTableId)
        .then((data) => {
          const tableTypes = data.data.contents.map((table) => {
            if (table.table_style) {
              const style = JSON.parse(table.table_style);
              const pixelsPerCm = GRID_SIZE / REAL_GRID_SIZE;
              return {
                ...style,
                x: style.x * pixelsPerCm,
                y: style.y * pixelsPerCm,
                width: style.width * pixelsPerCm,
                height: style.height * pixelsPerCm,
                rotation: style.rotation || 0,
                flipX: style.flipX || false,
                flipY: style.flipY || false
              };
            }
            return {
              id: table.table_id,
              name: table.table_name,
              x: 0,
              y: 0,
              width: 80,
              height: 80,
              rotation: 0,
              flipX: false,
              flipY: false,
              imageUrl: roundTable,
            };
          });
          setTables(tableTypes);
        });
    }
  }, [weddingTableId]);

  useEffect(() => {
    loadTableStyle();
  }, [loadTableStyle]);

  useEffect(() => {
    const container = document.querySelector(".tables-container");
    if (container) {
      const pixelsPerCm = calculatePixelsPerCm();
      const displayWidth = layoutWidth * pixelsPerCm;
      const displayHeight = layoutHeight * pixelsPerCm;

      (container as HTMLElement).style.width = `${displayWidth}px`;
      (container as HTMLElement).style.height = `${displayHeight}px`;
      (container as HTMLElement).style.backgroundSize =
        `${GRID_SIZE}px ${GRID_SIZE}px`;
    }
  }, [layoutWidth, layoutHeight]);

  useEffect(() => {
    if (tables.length > 0) {
      applyTableStyles();
      applyTableNameCorrection();
    }
  }, [tables, applyTableStyles, applyTableNameCorrection]);

  const handleDragEnd = (tableId: string, x: number, y: number) => {
    setTables((prevTables) =>
      prevTables.map((table) => {
        if (table.id === tableId) {
          return { ...table, x, y };
        }
        return table;
      }),
    );
  };

  const handleResize = (tableId: string, width: number, height: number) => {
    setTables((prevTables) =>
      prevTables.map((table) => {
        if (table.id === tableId) {
          return { ...table, width, height };
        }
        return table;
      }),
    );
  };

  const handleRotate = (tableId: string, rotation: number) => {
    setTables((prevTables) =>
      prevTables.map((table) => {
        if (table.id === tableId) {
          return { ...table, rotation };
        }
        return table;
      }),
    );
  };

  const handleSave = () => {
    const pixelsPerCm = GRID_SIZE / REAL_GRID_SIZE;

    // 保存场地尺寸、图片信息、新人姓名和婚礼日期
    if (weddingTableId) {
      api.weddings.patchWeddingsWeddingTableIdWeddingTableWeddingTableIdPatch(weddingTableId, {
        wedding_style: JSON.stringify({
          width: layoutWidth,
          height: layoutHeight,
          images: images.map(img => ({
            ...img,
            x: img.x / pixelsPerCm,
            y: img.y / pixelsPerCm,
            width: img.width / pixelsPerCm,
            height: img.height / pixelsPerCm
          })),
          coupleNames,
          coupleNamesFont,
          coupleNamesFontSize,
          coupleNamesPosition: {
            x: coupleNamesPosition.x / pixelsPerCm,
            y: coupleNamesPosition.y / pixelsPerCm
          },
          weddingDate,
          weddingDateFont,
          weddingDateFontSize,
          weddingDatePosition: {
            x: weddingDatePosition.x / pixelsPerCm,
            y: weddingDatePosition.y / pixelsPerCm
          }
        })
      });
    }

    // 保存桌子布局
    tables.map((table) => {
      // 将像素转换为厘米
      const realX = table.x / pixelsPerCm;
      const realY = table.y / pixelsPerCm;
      const realWidth = table.width / pixelsPerCm;
      const realHeight = table.height / pixelsPerCm;

      api.tables.patchTablesTableIdTablesTableIdPatch(table.id, {
        table_style: JSON.stringify({
          ...table,
          x: realX,
          y: realY,
          width: realWidth,
          height: realHeight,
          rotation: table.rotation || 0,
          flipX: table.flipX || false,
          flipY: table.flipY || false,
        }),
      });
      return table;
    });
  };

  const handleReset = () => {
    loadTableStyle();
  };

  const handleScaleDragStart = (e: React.MouseEvent) => {
    e.preventDefault(); // 防止文本选择
    const startX = e.clientX;
    const startY = e.clientY;
    const startPosX = scalePosition.x;
    const startPosY = scalePosition.y;

    const handleMouseMove = (e: MouseEvent) => {
      const newX = startPosX - (startX - e.clientX);
      const newY = startPosY - (startY - e.clientY);
      setScalePosition({
        x: newX,
        y: newY,
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newImage: ImageType = {
          id: Date.now().toString(),
          url: e.target?.result as string,
          x: 0,
          y: 0,
          width: 200,
          height: 200
        };
        setImages(prev => [...prev, newImage]);
      };
      reader.readAsDataURL(file);
    }
  };

  // 渲染边栏内容
  const renderDrawerContent = () => {
    return (
      <div className="drawer-content">
        <h3>编辑</h3>
        
        <div className="drawer-section">
          <h4>新人姓名</h4>
          <div className="couple-names-input drawer-input-group">
            <label>
              姓名:
              <input
                type="text"
                value={coupleNames}
                onChange={(e) => setCoupleNames(e.target.value)}
                placeholder="输入新人姓名"
                className="drawer-input"
              />
            </label>
            
            <label>
              字体:
              <div className="font-selector-container">
                <FontSelector
                  value={coupleNamesFont}
                  onChange={setCoupleNamesFont}
                  options={fontOptions}
                />
              </div>
            </label>
            
            <label>
              字号:
              <input
                type="number"
                value={coupleNamesFontSize}
                onChange={(e) => setCoupleNamesFontSize(Number(e.target.value))}
                min="8"
                max="72"
                className="drawer-input drawer-input-small"
              />
            </label>
          </div>
        </div>
        
        <div className="drawer-section">
          <h4>婚礼日期</h4>
          <div className="wedding-date-input drawer-input-group">
            <label>
              日期:
              <input
                type="text"
                value={weddingDate}
                onChange={(e) => setWeddingDate(e.target.value)}
                placeholder="输入婚礼日期"
                className="drawer-input"
              />
            </label>
            
            <label>
              字体:
              <FontSelector
                value={weddingDateFont}
                onChange={setWeddingDateFont}
                options={fontOptions}
              />
            </label>
            
            <label>
              字号:
              <input
                type="number"
                value={weddingDateFontSize}
                onChange={(e) => setWeddingDateFontSize(Number(e.target.value))}
                min="8"
                max="72"
                className="drawer-input drawer-input-small"
              />
            </label>
          </div>
        </div>
        
        <div className="drawer-section">
          <h4>添加图片</h4>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: 'none' }}
            id="image-upload"
          />
          <Button 
            onClick={() => document.getElementById('image-upload')?.click()}
            className="drawer-button"
          >
            选择图片
          </Button>
        </div>
      </div>
    );
  };

  // 完全重写DraggableCoupleNames组件，使用与DraggableImage相同的拖动逻辑
  const DraggableCoupleNames = () => {
    const [isDragging, setIsDragging] = useState(false);
    const nameRef = useRef<HTMLDivElement>(null);
    const dragOffset = useRef({ x: 0, y: 0 });

    const handleMouseDown = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      
      if (nameRef.current) {
        const rect = nameRef.current.getBoundingClientRect();
        dragOffset.current = {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        };
        setIsDragging(true);
      }
    };

    useEffect(() => {
      const handleMouseMove = (e: MouseEvent) => {
        if (isDragging && nameRef.current) {
          const parent = nameRef.current.offsetParent as HTMLElement;
          if (parent) {
            const parentRect = parent.getBoundingClientRect();
            const x = e.clientX - parentRect.left - dragOffset.current.x;
            const y = e.clientY - parentRect.top - dragOffset.current.y;
            
            // 直接修改DOM元素样式，不更新状态
            nameRef.current.style.left = `${x}px`;
            nameRef.current.style.top = `${y}px`;
          }
        }
      };

      const handleMouseUp = () => {
        if (isDragging && nameRef.current) {
          // 拖动结束后，从DOM样式中获取最终位置，然后更新状态
          const left = parseInt(nameRef.current.style.left || '0');
          const top = parseInt(nameRef.current.style.top || '0');
          setCoupleNamesPosition({ x: left, y: top });
        }
        setIsDragging(false);
      };

      if (isDragging) {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      }

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }, [isDragging]);

    // 初始化位置
    useEffect(() => {
      if (nameRef.current && coupleNames && 
          (coupleNamesPosition.x === 0 || coupleNamesPosition.y === 0)) {
        const container = document.querySelector('.tables-container');
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const newX = containerRect.width / 2 - (nameRef.current.offsetWidth / 2);
          
          // 直接更新状态
          setCoupleNamesPosition({
            x: newX,
            y: 20
          });
        }
      }
    }, [coupleNames]);

    return (
      <div
        ref={nameRef}
        className="couple-names-display"
        style={{
          position: 'absolute',
          left: `${coupleNamesPosition.x}px`,
          top: `${coupleNamesPosition.y}px`,
          fontFamily: coupleNamesFont,
          fontSize: `${coupleNamesFontSize}px`,
          cursor: 'grab',
          zIndex: 2
        }}
        onMouseDown={handleMouseDown}
      >
        {coupleNames || "新郎 & 新娘"}
      </div>
    );
  };

  // 添加可拖动婚礼日期组件
  const DraggableWeddingDate = () => {
    const [isDragging, setIsDragging] = useState(false);
    const dateRef = useRef<HTMLDivElement>(null);
    const dragOffset = useRef({ x: 0, y: 0 });

    const handleMouseDown = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      
      if (dateRef.current) {
        const rect = dateRef.current.getBoundingClientRect();
        dragOffset.current = {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        };
        setIsDragging(true);
      }
    };

    useEffect(() => {
      const handleMouseMove = (e: MouseEvent) => {
        if (isDragging && dateRef.current) {
          const parent = dateRef.current.offsetParent as HTMLElement;
          if (parent) {
            const parentRect = parent.getBoundingClientRect();
            const x = e.clientX - parentRect.left - dragOffset.current.x;
            const y = e.clientY - parentRect.top - dragOffset.current.y;
            
            // 直接修改DOM元素样式，不更新状态
            dateRef.current.style.left = `${x}px`;
            dateRef.current.style.top = `${y}px`;
          }
        }
      };

      const handleMouseUp = () => {
        if (isDragging && dateRef.current) {
          // 拖动结束后，从DOM样式中获取最终位置，然后更新状态
          const left = parseInt(dateRef.current.style.left || '0');
          const top = parseInt(dateRef.current.style.top || '0');
          setWeddingDatePosition({ x: left, y: top });
        }
        setIsDragging(false);
      };

      if (isDragging) {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      }

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }, [isDragging]);

    // 初始化位置
    useEffect(() => {
      if (dateRef.current && weddingDate && 
          (weddingDatePosition.x === 0 || weddingDatePosition.y === 0)) {
        const container = document.querySelector('.tables-container');
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const newX = containerRect.width / 2 - (dateRef.current.offsetWidth / 2);
          
          // 直接更新状态
          setWeddingDatePosition({
            x: newX,
            y: 60 // 默认位置在新人姓名下方
          });
        }
      }
    }, [weddingDate]);

    return (
      <div
        ref={dateRef}
        className="wedding-date-display"
        style={{
          position: 'absolute',
          left: `${weddingDatePosition.x}px`,
          top: `${weddingDatePosition.y}px`,
          fontFamily: weddingDateFont,
          fontSize: `${weddingDateFontSize}px`,
          cursor: 'grab',
          zIndex: 2
        }}
        onMouseDown={handleMouseDown}
      >
        {weddingDate}
      </div>
    );
  };

  // 彻底解决对话框重新渲染问题和翻转功能
  const TableEditDialog = React.memo(({ 
    table, 
    onClose, 
    onUpdate 
  }: { 
    table: TableType; 
    onClose: () => void;
    onUpdate: (tableId: string, updates: Partial<TableType>) => void;
  }) => {
    // 保存桌子的原始状态 - 使用useRef确保在组件生命周期内保持不变
    const originalTableRef = useRef<TableType | null>(null);
    
    // 初始化originalTableRef
    useEffect(() => {
      if (!originalTableRef.current) {
        originalTableRef.current = {...table};
      }
    }, [table]);
    
    // 使用ref存储桌子的所有属性，避免状态更新
    const tableRef = useRef({...table});
    const formRef = useRef<HTMLFormElement>(null);
    
    // DOM引用
    const nameInputRef = useRef<HTMLInputElement>(null);
    const xInputRef = useRef<HTMLInputElement>(null);
    const yInputRef = useRef<HTMLInputElement>(null);
    const widthInputRef = useRef<HTMLInputElement>(null);
    const heightInputRef = useRef<HTMLInputElement>(null);
    const rotationInputRef = useRef<HTMLInputElement>(null);
    const flipXInputRef = useRef<HTMLInputElement>(null);
    const flipYInputRef = useRef<HTMLInputElement>(null);
    
    // 添加表格样式选择状态
    const [selectedTableStyle, setSelectedTableStyle] = useState<number>(() => {
      // 确定当前桌子样式
      const styleIndex = Object.entries(tableStyleMap).findIndex(
        ([_, url]) => url === table.imageUrl
      );
      return styleIndex !== -1 ? styleIndex + 1 : 1;
    });
    
    // 保存原始选中的样式
    const originalStyleRef = useRef(selectedTableStyle);

    // 直接在DOM中更新桌子，完全绕过React状态系统
    const updateTableDirectly = (property: string, value: any) => {
      const pixelsPerCm = GRID_SIZE / REAL_GRID_SIZE;
      
      // 更新ref中的值
      (tableRef.current as any)[property] = value;
      
      // 仅在DOM中更新，不触发React重新渲染
      if (property === 'name' && nameInputRef.current) {
        nameInputRef.current.value = value;
      } else if (property === 'x' && xInputRef.current) {
        // 不再格式化为固定小数位
        xInputRef.current.value = String(value / pixelsPerCm);
      } else if (property === 'y' && yInputRef.current) {
        yInputRef.current.value = String(value / pixelsPerCm);
      } else if (property === 'width' && widthInputRef.current) {
        widthInputRef.current.value = String(value / pixelsPerCm);
      } else if (property === 'height' && heightInputRef.current) {
        heightInputRef.current.value = String(value / pixelsPerCm);
      } else if (property === 'rotation' && rotationInputRef.current) {
        rotationInputRef.current.value = value;
      } else if (property === 'flipX' && flipXInputRef.current) {
        flipXInputRef.current.checked = value;
      } else if (property === 'flipY' && flipYInputRef.current) {
        flipYInputRef.current.checked = value;
      }
    };

    // 处理表单提交
    const handleSubmit = useCallback((e: React.FormEvent) => {
      e.preventDefault();
      onUpdate(tableRef.current.id, tableRef.current);
      onClose();
    }, [onClose, onUpdate]);

    // 处理取消按钮
    const handleCancel = useCallback(() => {
      // 取消编辑，恢复原始状态
      if (originalTableRef.current) {
        onUpdate(originalTableRef.current.id, originalTableRef.current);
      }
      onClose();
    }, [onClose, onUpdate]);

    // 确保在对话框打开时初始化输入字段的值（改为厘米单位）
    useEffect(() => {
      const pixelsPerCm = GRID_SIZE / REAL_GRID_SIZE;
      
      if (nameInputRef.current) nameInputRef.current.value = table.name;
      if (xInputRef.current) xInputRef.current.value = String(table.x / pixelsPerCm);
      if (yInputRef.current) yInputRef.current.value = String(table.y / pixelsPerCm);
      if (widthInputRef.current) widthInputRef.current.value = String(table.width / pixelsPerCm);
      if (heightInputRef.current) heightInputRef.current.value = String(table.height / pixelsPerCm);
      if (rotationInputRef.current) rotationInputRef.current.value = String(table.rotation || 0);
      if (flipXInputRef.current) flipXInputRef.current.checked = table.flipX || false;
      if (flipYInputRef.current) flipYInputRef.current.checked = table.flipY || false;
      
      // 更新tableRef以匹配当前table
      tableRef.current = {...table};
    }, [table]);

    return (
      <div className="table-edit-dialog">
        <button onClick={onClose} className="close-button">×</button>
        <h4>编辑桌子</h4>
        
        <form ref={formRef} onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Table Name</label>
            <input
              ref={nameInputRef}
              type="text"
              defaultValue={table.name}
              onChange={(e) => updateTableDirectly('name', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            {/* <label>位置和尺寸 (厘米)</label> */}
            <div className="input-grid">
              <div>
                <label>X-axis (cm)</label>
                <input
                  ref={xInputRef}
                  type="number"
                  defaultValue={String(table.x / (GRID_SIZE / REAL_GRID_SIZE))}
                  onChange={(e) => {
                    const cmValue = Number(e.target.value);
                    updateTableDirectly('x', cmValue * (GRID_SIZE / REAL_GRID_SIZE));
                  }}
                  step="0.1"
                />
              </div>
              <div>
                <label>Y-axis (cm)</label>
                <input
                  ref={yInputRef}
                  type="number"
                  defaultValue={String(table.y / (GRID_SIZE / REAL_GRID_SIZE))}
                  onChange={(e) => {
                    const cmValue = Number(e.target.value);
                    updateTableDirectly('y', cmValue * (GRID_SIZE / REAL_GRID_SIZE));
                  }}
                  step="0.1"
                />
              </div>
              <div>
                <label>Width (cm)</label>
                <input
                  ref={widthInputRef}
                  type="number"
                  defaultValue={String(table.width / (GRID_SIZE / REAL_GRID_SIZE))}
                  onChange={(e) => {
                    const cmValue = Number(e.target.value);
                    updateTableDirectly('width', cmValue * (GRID_SIZE / REAL_GRID_SIZE));
                  }}
                  step="0.1"
                />
              </div>
              <div>
                <label>Height (cm)</label>
                <input
                  ref={heightInputRef}
                  type="number"
                  defaultValue={String(table.height / (GRID_SIZE / REAL_GRID_SIZE))}
                  onChange={(e) => {
                    const cmValue = Number(e.target.value);
                    updateTableDirectly('height', cmValue * (GRID_SIZE / REAL_GRID_SIZE));
                  }}
                  step="0.1"
                />
              </div>
            </div>
          </div>
          
          <div className="form-group">
            <label>Rotation (°)</label>
            <input
              ref={rotationInputRef}
              type="number"
              defaultValue={table.rotation || 0}
              onChange={(e) => updateTableDirectly('rotation', Number(e.target.value))}
            />
          </div>
          
          <div className="form-group flip-controls">
            <div className="checkbox-group">
              <label>
                <input
                  ref={flipXInputRef}
                  type="checkbox"
                  defaultChecked={table.flipX || false}
                  onChange={(e) => updateTableDirectly('flipX', e.target.checked)}
                />
                水平翻转
              </label>
            </div>
            <div className="checkbox-group">
              <label>
                <input
                  ref={flipYInputRef}
                  type="checkbox"
                  defaultChecked={table.flipY || false}
                  onChange={(e) => updateTableDirectly('flipY', e.target.checked)}
                />
                垂直翻转
              </label>
            </div>
          </div>
          
          <div className="form-group">
            <label>桌子样式</label>
            <div className="table-style-grid">
              {[1, 2, 3, 4, 5, 6, 7].map((index) => (
                <div
                  key={index}
                  className={`table-style-image-container${index === selectedTableStyle ? " selected" : ""}`}
                  onClick={() => {
                    setSelectedTableStyle(index);
                    updateTableDirectly('imageUrl', tableStyleMap[index as keyof typeof tableStyleMap]);
                  }}
                >
                  <img
                    src={tableStyleMap[index as keyof typeof tableStyleMap]}
                    alt={`桌子样式 ${index}`}
                    className="table-style-image"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="button-group">
            <button type="button" className="cancel-button" onClick={handleCancel}>
              Cancel
            </button>
            <button type="submit" className="save-button">
              Save
            </button>
          </div>
        </form>
      </div>
    );
  }, (prevProps, nextProps) => {
    // 修改比较函数，仅当id变化或对话框切换时重新渲染
    return prevProps.table.id === nextProps.table.id;
  });

  return (
    <div>
      <div className="button-container">
        <Button
          onClick={() => navigate(`/wedding_table/${weddingTableId}/table-list`)}
          className="back-button"
        >
          <i className="fa fa-angle-left"></i> Back
        </Button>
        <div className="size-inputs">
          <label>
            Venue Width (cm):
            <input
              className="size-input"
              type="number"
              value={layoutWidth}
              onChange={(e) => setLayoutWidth(Number(e.target.value))}
              placeholder="Width"
            />
          </label>
          <label>
            Venue Height (cm):
            <input
              className="size-input"
              type="number"
              value={layoutHeight}
              onChange={(e) => setLayoutHeight(Number(e.target.value))}
              placeholder="Height"
            />
          </label>
        </div>
        <Button onClick={handleReset} className="reset-button">
          Reset
        </Button>
        <Button onClick={handleSave} className="save-button">
          Save
        </Button>
        <Button onClick={toggleDrawer} className="edit-button">
          Edit
        </Button>
      </div>
      
      {/* 侧边栏 */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer}
      >
        {renderDrawerContent()}
      </Drawer>
      
      <DndProvider backend={HTML5Backend}>
        <div className="table-layout-page">
          <div className="tables-container-wrapper">
            <div className="tables-container">
              {coupleNames && <DraggableCoupleNames />}
              {weddingDate && <DraggableWeddingDate />}
              {images.map(image => (
                <DraggableImage
                  key={image.id}
                  image={image}
                  onDragEnd={(id, x, y) => {
                    setImages(prev => prev.map(img => 
                      img.id === id ? { ...img, x, y } : img
                    ));
                  }}
                  onResize={(id, width, height) => {
                    setImages(prev => prev.map(img =>
                      img.id === id ? { ...img, width, height } : img
                    ));
                  }}
                  onDelete={(id) => {
                    setImages(prev => prev.filter(img => img.id !== id));
                  }}
                />
              ))}
              {tables.map((table) => (
                <DraggableTableIcon
                  key={table.id}
                  table={table}
                  onDragEnd={handleDragEnd}
                  onResize={handleResize}
                  onRotate={handleRotate}
                  disableDragResize={false}
                  onClick={(e) => {
                    setSelectedTable(table);
                  }}
                />
              ))}
            </div>
          </div>
          <div
            className="scale-indicator"
            onMouseDown={handleScaleDragStart}
            style={{
              left: `${scalePosition.x}px`,
              top: `${scalePosition.y}px`,
            }}
          >
            <div className="scale-line">
              <span></span>
            </div>
            <span>100cm</span>
          </div>
        </div>
      </DndProvider>

      {selectedTable && (
        <TableEditDialog
          table={selectedTable}
          onClose={() => setSelectedTable(null)}
          onUpdate={(tableId, updates) => {
            // 创建新的表格数组，确保引用变化触发重新渲染
            const updatedTables = tables.map(table =>
              table.id === tableId ? { ...table, ...updates } : table
            );
            
            // 更新状态
            setTables(updatedTables);
            
            // 同时更新 selectedTable 以在对话框中显示最新状态
            if (selectedTable && selectedTable.id === tableId) {
              setSelectedTable({ ...selectedTable, ...updates });
            }
          }}
        />
      )}
    </div>
  );
}

export default WeddingTableLayoutPage;
